import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useGibRating } from "@/hooks/useGibRating";
import { GibletGuide } from "./GibletGuide";
import { SprintGibRatingSummary } from "./SprintGibRatingSummary";
import { SprintReviewDashboard } from "./SprintReviewDashboard";

interface AssessStageProps {
  sprintId: string;
  userId: string;
  onComplete: () => void;
}

export const AssessStage = ({ sprintId, userId, onComplete }: AssessStageProps) => {
  const { toast } = useToast();
  const { calculateGibRating, isCalculating } = useGibRating(userId);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [contributions, setContributions] = useState<any[]>([]);

  useEffect(() => {
    loadContributions();
  }, [sprintId]);

  const loadContributions = async () => {
    try {
      // Load all sprint contributions to get contributor list
      const { data, error } = await supabase
        .from('sprint_contributions')
        .select('contributor_id')
        .eq('sprint_id', sprintId);

      if (error) throw error;
      setContributions(data || []);
    } catch (error: any) {
      toast({
        title: "Failed to load contributions",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteStage = async () => {
    try {
      // Mark sprint as complete
      const { error: sprintError } = await supabase
        .from('prompt_architecture_sprints')
        .update({ 
          assess_stage_completed: true,
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', sprintId);

      if (sprintError) throw sprintError;

      // Trigger GibRating calculation for all contributors
      const contributorIds = [...new Set(contributions.map(c => c.contributor_id))];
      
      for (const contributorId of contributorIds) {
        calculateGibRating(contributorId);
      }

      setCompleted(true);
      
      toast({ 
        title: "Sprint completed!",
        description: "GibRatings are being recalculated for all participants."
      });

      setTimeout(() => onComplete(), 2000);
    } catch (error: any) {
      toast({
        title: "Failed to complete stage",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Card>
    );
  }

  if (completed) {
    return <SprintGibRatingSummary sprintId={sprintId} />;
  }

  return (
    <div className="space-y-4">
      <GibletGuide stage="assess" />
      
      <div className="space-y-6">
        <div>
          <h3 className="text-xl font-bold mb-2">Stage 4: React & Evolve</h3>
          <p className="text-muted-foreground">
            Review all participant contributions across all stages and provide feedback to build collaborative reputation.
          </p>
        </div>

        <SprintReviewDashboard sprintId={sprintId} userId={userId} />

        <Card className="p-4">
          <Button
            onClick={handleCompleteStage}
            size="lg"
            className="w-full"
            disabled={isCalculating}
          >
            {isCalculating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Calculating GibRatings...
              </>
            ) : (
              'Complete Sprint & Update GibRatings'
            )}
          </Button>
        </Card>
      </div>
    </div>
  );
};
